import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {QuestionOptions} from '../../../constants/question-options';
import {Proposal} from '../../../model/proposal/proposal';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {CustomValidators} from '../../../validator/custom-validator';
import {getCurrencySymbol} from '@angular/common';
import {ReplaySubject, takeUntil} from 'rxjs';
import {ProposalInformation} from '../../../model/proposal/proposal-information';

@Component({
    selector: 'app-amount-insured-form',
    templateUrl: './amount-insured-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: AmountInsuredFormComponent }],
    standalone: false
})
export class AmountInsuredFormComponent extends ProposalFormBase implements OnInit, AfterViewInit {

  @Input() proposalPreviewInformation: ProposalInformation;
  @Input() isEditRoute: boolean = false;
  @Output() scrollToSkendata = new EventEmitter<void>();

  private _selectedLineOfBusinessNumber: string = null;

  @Input() set selectedLineOfBusinessNumber(lob: string) {
    this._selectedLineOfBusinessNumber = lob;
    const ctrl = this.formGroup.controls.insuredSumType;
    const isHousing = lob === QuestionOptions.HOUSING_INSURANCE_LOB_NUMBER;
    if (isHousing) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      if (!this.isEditRoute) {
        ctrl.setValue(null);
      }
    }
    ctrl.updateValueAndValidity();
  }

  get isHousingInsuranceLob(): boolean {
    return this._selectedLineOfBusinessNumber === QuestionOptions.HOUSING_INSURANCE_LOB_NUMBER;
  }

  get isValues1914Selected(): boolean {
    return this.isHousingInsuranceLob && this.formGroup.value.insuredSumType === 'VALUES_1914';
  }

  formGroup = this.fb.group({
    insuredSumType: new FormControl<string>(null),
    insuredAmount: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber()]),
    currency: new FormControl<string>('EUR', Validators.required),
    amountDescription: new FormControl<string>(''),
    annualSalesAmount: new FormControl<number>(null, [CustomValidators.validNumber({allowZero: true})]),
    annualSalesDescription: new FormControl<string>('')
  });
  maxNumber = 5;
  QuestionOptions = QuestionOptions;

  public filterCtrlCurrency: FormControl<string> = new FormControl<string>('');
  public filteredOptionsCurrency: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  currencyOptions: string[] = QuestionOptions.currency;

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    if (this.proposalPreviewInformation != null) {
      this.formGroup.controls.insuredSumType.patchValue(this.proposalPreviewInformation.insuredSumType);
      this.formGroup.controls.insuredAmount.patchValue(this.proposalPreviewInformation.insuredAmount);
      this.formGroup.controls.currency.patchValue(this.proposalPreviewInformation.insuredAmountCurrency);
      this.formGroup.controls.amountDescription.patchValue(this.proposalPreviewInformation.amountDescription);
      this.formGroup.controls.annualSalesAmount.patchValue(this.proposalPreviewInformation.annualSalesAmount);
      this.formGroup.controls.annualSalesDescription.patchValue(this.proposalPreviewInformation.annualSalesDescription);
    }
    this.filteredOptionsCurrency.next(this.currencyOptions);
    this.filterCtrlCurrency.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCurrencies(this.filterCtrlCurrency.value, this.currencyOptions, this.filteredOptionsCurrency);
      });
  }

  filterCurrencies(search: string, options, filtered) {
    if (!options) { return; }
    if (!search) { filtered.next(options.slice()); return; }
    else { search = search.toLowerCase(); }
    filtered.next(options.filter(country => country.toLowerCase().indexOf(search) > -1));
  }

  updateProposal(proposal: Proposal): void {
    proposal.proposalInformation.insuredSumType = this.formGroup.value.insuredSumType;
    proposal.proposalInformation.insuredAmount = this.formGroup.value.insuredAmount;
    proposal.proposalInformation.insuredAmountCurrency = this.formGroup.value.currency;
    proposal.proposalInformation.amountDescription = this.formGroup.value.amountDescription;
    proposal.proposalInformation.annualSalesAmount = this.formGroup.value.annualSalesAmount;
    proposal.proposalInformation.annualSalesDescription = this.formGroup.value.annualSalesDescription;
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.insuredSumType.patchValue(proposal.proposalInformation.insuredSumType);
      this.formGroup.controls.insuredAmount.patchValue(proposal.proposalInformation.insuredAmount);
      this.formGroup.controls.currency.patchValue(proposal.proposalInformation.insuredAmountCurrency);
      this.formGroup.controls.amountDescription.patchValue(proposal.proposalInformation.amountDescription);
      this.formGroup.controls.annualSalesAmount.patchValue(proposal.proposalInformation.annualSalesAmount);
      this.formGroup.controls.annualSalesDescription.patchValue(proposal.proposalInformation.annualSalesDescription);
    }
  }

  autoFillForEdit(proposal: Proposal): void {
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.insuredSumType.patchValue(proposal.proposalInformation.insuredSumType);
      this.formGroup.controls.insuredAmount.patchValue(proposal.proposalInformation.insuredAmount);
      this.formGroup.controls.currency.patchValue(proposal.proposalInformation.insuredAmountCurrency);
      this.formGroup.controls.amountDescription.patchValue(proposal.proposalInformation.amountDescription);
      this.formGroup.controls.annualSalesAmount.patchValue(proposal.proposalInformation.annualSalesAmount);
      this.formGroup.controls.annualSalesDescription.patchValue(proposal.proposalInformation.annualSalesDescription);
    }
  }

  currencySymbol() {
    return getCurrencySymbol(this.formGroup.value.currency, 'narrow');
  }

  reset() {
    this.formGroup.reset();
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
    this.formGroup.controls.currency.patchValue('EUR');
  }

  formatInsuredAmount() {
    const inputElement = document.querySelector('input[formControlName=\'insuredAmount\']');
    if (inputElement) { inputElement.dispatchEvent(new Event('convertNumber')); }
  }

  formatAnnualSalesAmount() {
    const inputElement = document.querySelector('input[formControlName=\'annualSalesAmount\']');
    if (inputElement) { inputElement.dispatchEvent(new Event('convertNumber')); }
  }

  ngAfterViewInit(): void {
    this.formatInsuredAmount();
    this.formatAnnualSalesAmount();
  }
}
